import React from "react";
import {
  MdAccountBalanceWallet,
  MdTrendingUp,
  MdReceiptLong,
  MdInventory2,
} from "react-icons/md";

const formatAmount = (n) =>
  Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 });

const formatRs = (n, loading) => {
  if (loading) return "…";
  return `Rs ${formatAmount(n)}`;
};

const STAT_ITEMS = [
  {
    id: "totalCartons",
    label: "Total cartons",
    desc: "Purchased from all companies (Box)",
    icon: MdInventory2,
    iconClass: "text-sky-200",
    valueClass: "text-sky-100",
    cardClass: "bg-sky-400/10 border border-sky-300/20",
    isQty: true,
  },
  {
    id: "total",
    label: "Total",
    desc: "Total purchase amount",
    icon: MdReceiptLong,
    iconClass: "text-white/90",
    valueClass: "text-white",
    cardClass: "bg-white/10",
  },
  {
    id: "paid",
    label: "Paid",
    desc: "Amount paid to company",
    icon: MdTrendingUp,
    iconClass: "text-emerald-300",
    valueClass: "text-emerald-200",
    cardClass: "bg-white/10",
  },
  {
    id: "remaining",
    label: "Remaining",
    desc: "Still payable",
    icon: MdAccountBalanceWallet,
    iconClass: "text-amber-200",
    valueClass: "text-amber-100",
    cardClass: "bg-amber-400/15 border border-amber-300/30",
  },
];

const PayablesSummaryHero = ({ summary, loading }) => {
  const paymentPct =
    summary.total > 0
      ? Math.min(100, Math.round((summary.paid / summary.total) * 100))
      : 0;

  return (
    <div className="shrink-0 rounded-2xl bg-gradient-to-br from-[#39454F] via-[#2d363f] to-[#1f262d] text-white shadow-card mb-4 overflow-hidden">
      <div className="px-3.5 py-3 max720:px-3 max720:py-2.5 border-b border-white/10">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
              <MdAccountBalanceWallet size={17} />
            </div>
            <div className="min-w-0">
              <h2 className="text-xs font-bold uppercase tracking-wide leading-tight">
                Payables summary
              </h2>
              <p className="text-[10px] text-white/65 leading-tight truncate">
                {summary.count} compan{summary.count !== 1 ? "ies" : "y"} with
                pending payable
              </p>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[9px] uppercase tracking-wider text-white/55 font-semibold leading-none">
              Payment rate
            </p>
            <p className="text-lg font-extrabold text-emerald-200 leading-tight">
              {loading ? "…" : `${paymentPct}%`}
            </p>
          </div>
        </div>

        <div className="mt-2.5">
          <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-300 transition-all duration-500"
              style={{ width: loading ? "0%" : `${paymentPct}%` }}
            />
          </div>
          <div className="flex justify-between mt-1 text-[9px] text-white/50 tabular-nums">
            <span>Paid {loading ? "…" : formatAmount(summary.paid)}</span>
            <span>Remaining {loading ? "…" : formatAmount(summary.remaining)}</span>
          </div>
        </div>
      </div>

      <div className="p-2.5 max720:p-2 grid grid-cols-2 gap-1.5">
        {STAT_ITEMS.map(
          ({ id, label, desc, icon: Icon, iconClass, valueClass, cardClass, isQty }) => (
            <div
              key={id}
              className={`rounded-lg px-2.5 py-2 max720:px-2 max720:py-1.5 ${cardClass}`}
            >
              <div className="flex items-start justify-between gap-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1">
                    <Icon size={13} className={`shrink-0 ${iconClass}`} />
                    <span className="text-[10px] font-bold uppercase tracking-wide text-white/85 leading-tight">
                      {label}
                    </span>
                  </div>
                  <p className="text-[9px] text-white/50 mt-0.5 leading-snug line-clamp-2">
                    {desc}
                  </p>
                </div>
              </div>
              <p
                className={`mt-1 text-sm max720:text-xs font-extrabold tabular-nums leading-tight ${valueClass}`}
              >
                {isQty
                  ? loading
                    ? "…"
                    : formatAmount(summary[id])
                  : formatRs(summary[id], loading)}
              </p>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default PayablesSummaryHero;
